import type { Dictionary } from './types'

const fr: Dictionary = {
  common: {
    appName: 'Pivot',
    signIn: 'Se connecter avec Google',
    signOut: 'Se déconnecter',
    loading: 'Chargement…',
    save: 'Enregistrer',
    cancel: 'Annuler',
    retry: 'Réessayer',
    error: "Une erreur s'est produite.",
    reload: 'Recharger la page',
  },
  landing: {
    metaDescription:
      "Pivot lit chaque offre des job boards de niche belges, francophones et néerlandophones, note chacune par rapport à votre CV et ne vous montre que celles qui collent — y compris les pivots latéraux que vous n'auriez jamais cherchés.",
    heroTitle: "L'IA a pris votre emploi. Faites-lui trouver le prochain.",
    subtitle:
      'Pivot lit votre CV, vous interroge sur ce que vous voudriez faire à la place, puis note chaque offre des boards de niche belges selon vos réponses. Chaque jour.',
    googleNote:
      'Google nous transmet votre nom et votre adresse e-mail. Il ne voit ni vos contacts, ni votre agenda, ni vos fichiers. Ça vous évite un mot de passe de plus.',
    scrollCue: 'Voir comment ça marche',
    shotDashboard: 'Le tableau de bord de Pivot, avec les offres du jour classées et leurs scores',
    turnEyebrow: 'Un exemple de pivot latéral',
    // Voir le commentaire dans en.ts. Intitulés réels du corpus : « Gestionnaire
    // logement » est verbatim (alterjob) ; « Médiateur de dettes » et « Chargé de
    // communication » sont la forme de base de « Médiateur·trice de dettes » et
    // « Chargé·e de communication » (alterjob, economiesociale) — le point médian
    // est du bruit visuel en display 40px, le poste est le même.
    turnFrom1: 'Comptable',
    turnTo1: 'Gestionnaire logement',
    turnFrom2: 'Employé de banque',
    turnTo2: 'Médiateur de dettes',
    turnFrom3: 'Graphiste',
    turnTo3: 'Chargé de communication',
    turnNote:
      "Vous ne l'auriez jamais tapé dans une barre de recherche. Chaque pivot arrive sur un intitulé réel, tiré des boards que Pivot lit.",
    interviewEyebrow: 'Pour commencer',
    interviewTitle: 'Il lit votre CV, puis demande ce qui manque',
    interviewBody:
      "Déposez votre CV et Pivot vous interroge : ce que vous faites bien, ce que vous voulez arrêter, les langues dans lesquelles vous travaillez, la direction qui vous tente. Il peut proposer des pistes auxquelles vous n'aviez pas pensé. Vous relisez le profil qu'il écrit et le corrigez en langage clair.",
    shotInterview: "L'entretien d'inscription, en cours de conversation",
    rankEyebrow: 'Chaque jour',
    rankTitle: 'Chaque offre lue en entier, notée sur trois axes',
    rankBody:
      "Claude ouvre chaque annonce et la lit de bout en bout. Chacune reçoit une note pour l'expérience, les valeurs et le salaire, une courte explication et une étiquette : un pas direct, ou un pas de côté.",
    rankHonest:
      'Quand rien ne sort, la liste est courte ou vide. Pivot ne la remplit jamais pour faire bonne figure.',
    shotMatch:
      'Quelques offres, chacune avec ses trois scores et son étiquette directe ou latérale',
    sourcesEyebrow: 'Où il cherche',
    sourcesTitle: "Les boards que vous n'avez pas le temps de visiter",
    sourcesBody:
      "La longue traîne belge : secteur social, culture, associations, universités, quartier européen. Éparpillée entre le français et le néerlandais, et jamais mise en face d'un profil. Pivot les lit tous, chaque matin.",
    sourcesFrLabel: 'Francophones',
    sourcesNlLabel: 'Néerlandophones',
    sourcesDeLabel: 'Germanophones',
    sourcesEnLabel: 'Anglophones',
    sourcesAside: 'LinkedIn et Indeed ne sont pas des sources. Vous y êtes déjà.',
    trustHeading: 'Non négociables',
    trustLangs: 'Français · Nederlands · English',
    trustConsent: "Votre CV n'est lu qu'après votre accord",
    trustErasure: 'Supprimez votre compte et tout son contenu, quand vous voulez',
    trustResidency: "Hébergé dans l'UE",
    closeTitle: 'Osez changer',
    closeBody: "Connectez-vous, déposez votre CV, et voyez ce que donne aujourd'hui.",
    footerRights: 'Tous droits réservés.',
    footerPrivacy: 'Politique de confidentialité',
  },
  nav: {
    dashboard: 'Tableau de bord',
    saved: 'Enregistrés',
    profile: 'Profil',
  },
  auth: {
    emailLabel: 'E-mail',
    passwordLabel: 'Mot de passe',
    newPasswordLabel: 'Nouveau mot de passe',
    orDivider: 'ou',
    emailSignInCta: 'Se connecter par e-mail',
    loginTitle: 'Connexion',
    loginCta: 'Se connecter',
    loginNoAccount: 'Pas encore de compte ?',
    loginCreateAccount: 'Créer un compte',
    loginForgot: 'Mot de passe oublié ?',
    signupTitle: 'Créer un compte',
    signupCta: 'Créer mon compte',
    signupHaveAccount: 'Déjà un compte ?',
    signupSignIn: 'Se connecter',
    signupPasswordHint: 'Au moins 8 caractères.',
    forgotTitle: 'Mot de passe oublié',
    forgotBody:
      'Indiquez votre e-mail : si un compte y correspond, vous recevrez un lien de réinitialisation.',
    forgotCta: 'Envoyer le lien',
    forgotSent:
      'Si cet e-mail correspond à un compte, un lien de réinitialisation vient de vous être envoyé.',
    backToLogin: 'Retour à la connexion',
    resetTitle: 'Nouveau mot de passe',
    resetBody: 'Choisissez un nouveau mot de passe pour votre compte.',
    resetCta: 'Réinitialiser',
    resetSuccess: 'Votre mot de passe a été mis à jour. Vous pouvez vous connecter.',
    resetInvalidToken: 'Ce lien est invalide ou a expiré. Redemandez-en un nouveau.',
    genericError: "Une erreur s'est produite. Réessayez.",
    sessionExpired: 'Votre session a expiré. Reconnectez-vous.',
    googleUnavailable:
      "La connexion Google n'est pas configurée pour cette version. Utilisez l'e-mail.",
  },
  onboarding: {
    pageTitle: 'Commencer',
    intentTitle: "Qu'est-ce qui vous amène ?",
    intentBody:
      'Choisissez ce qui vous correspond le mieux — ça nous aide simplement à poser de meilleures questions.',
    intentPivotNewSector: 'Se réorienter vers un nouveau secteur',
    intentSameSectorNewRole: 'Même secteur, un nouveau type de poste',
    intentKnowDirection: 'Je sais où je veux aller',
    intentUnsure: "Pas encore sûr·e — j'explore",
    consentTitle: 'Avant de commencer',
    consentBody:
      "Pivot peut envoyer le texte de votre CV, vos réponses, votre profil et les offres utiles à Anthropic, OpenAI, Voyage AI, OpenRouter et au fournisseur d'inférence choisi par OpenRouter. Consultez la politique de confidentialité pour la liste complète. Pivot chiffre au repos sa propre copie de votre CV. Pivot ne fait aucune affirmation sur ce que les fournisseurs cités font de ces textes. Pivot ne partage jamais vos données avec d'autres utilisateurs.",
    consentAccept: "J'accepte et je continue",
    consentDecline: 'Pas maintenant',
    consentPolicyLink: 'Lire la politique de confidentialité complète',
    declinedTitle: "Rien n'a été traité",
    declinedBody:
      "Aucun CV n'a été lu et aucune réponse n'a été enregistrée. Rien ne se passe tant que vous n'avez pas accepté. Revenez quand vous voulez — ou déconnectez-vous et laissez les choses là.",
    declinedBack: 'Revenir et relire',
    stepIntent: 'Votre raison',
    stepConsent: 'Consentement',
    stepUpload: 'Votre CV',
    stepInterview: 'La conversation',
    stepReview: 'Vérification',
    stepsInterviewNote:
      "Cette partie est une conversation : elle dure le temps qu'il faut — en général quelques questions.",
    backToProfile: 'Retour à mon profil',
    uploadTitle: 'Déposez votre CV',
    uploadBody: 'PDF de préférence — on en extrait votre parcours pour démarrer la conversation.',
    uploadCta: 'Glissez un PDF ici, ou choisissez un fichier',
    uploadFormatHint: "PDF, jusqu'à {{limit}} Mo",
    uploadInvalidType: "Ce fichier n'est pas un PDF. Choisissez un PDF.",
    uploadTooLarge: 'Ce fichier dépasse {{limit}} Mo. Choisissez un PDF plus léger et réessayez.',
    uploadRejectedType:
      "Le serveur n'a pas pu lire ce fichier comme un PDF — un .docx renommé ou une image donne ce résultat. Exportez votre CV en vrai PDF et ça passera.",
    uploadedAs: 'Fichier reçu :',
    uploadContinue: 'Continuer',
    cvReading: "Lecture de votre CV. Cela peut prendre quelques minutes, c'est normal.",
    cvReadingPatience:
      "Toujours en lecture. C'est normal : un modèle le lit vraiment, il ne cherche pas des mots-clés.",
    savedCvEyebrow: 'Déjà enregistré',
    savedCvBody:
      'Nous avons gardé votre CV et ce que nous en avons retenu. Pas besoin de le renvoyer.',
    savedCvUploadedOn: 'Envoyé le {{date}}',
    savedCvAnalysisLabel: 'Ce que nous en avons retenu',
    savedCvUse: 'Continuer avec ce CV',
    savedCvReplace: 'Envoyer un autre CV',
    savedCvKeep: 'Garder le CV enregistré',
    interviewTitle: 'Quelques questions',
    interviewIntro: 'Discutons un peu de ce que vous cherchez vraiment.',
    interviewPlaceholder: 'Écrivez votre réponse…',
    interviewWriteOption: 'En discuter — écrivez votre propre réponse',
    interviewMultiHint: 'Cochez tout ce qui correspond. Plusieurs réponses sont possibles ici.',
    interviewConfirm: 'Envoyer la sélection',
    interviewSend: 'Envoyer',
    interviewSendHint: 'Entrée pour envoyer · Maj + Entrée pour une nouvelle ligne',
    interviewThinking: 'Préparation de la question suivante',
    interviewThinkingPatience:
      'Toujours en cours — chaque question est choisie selon ce que vous avez dit, pas selon un script figé.',
    interviewConsentStale:
      'Votre consentement doit être renouvelé avant de continuer cet entretien. Vérifiez votre profil.',
    constraintsTitle: 'Où pouvez-vous travailler, concrètement ?',
    constraintsBody:
      "Court et factuel. Ce sont les limites que nous ne franchirons pas en choisissant vos matches — si l'une d'elles ne vous limite pas, dites-le explicitement.",
    constraintsLocationsLabel: 'Lieux où vous travailleriez',
    constraintsLocationsHint: 'Cochez chaque province où vous pourriez travailler.',
    constraintsLocationsAnywhere: "N'importe où me convient",
    provinceBrussels: 'Bruxelles',
    provinceAntwerp: 'Anvers',
    provinceLimburg: 'Limbourg',
    provinceEastFlanders: 'Flandre orientale',
    provinceWestFlanders: 'Flandre occidentale',
    provinceFlemishBrabant: 'Brabant flamand',
    provinceHainaut: 'Hainaut',
    provinceLiege: 'Liège',
    provinceNamur: 'Namur',
    provinceLuxembourg: 'Luxembourg',
    provinceWalloonBrabant: 'Brabant wallon',
    locationChipRemove: 'Retirer {{location}}',
    constraintsAnchorLabel: "D'où vous faites la navette",
    constraintsAnchorHint: 'Un seul lieu. Il détermine la distance réelle de chaque poste.',
    constraintsAnchorNone: 'Pas de base fixe',
    constraintsContractLabel: 'Contrats que vous accepteriez',
    constraintsContractCdi: 'CDI',
    constraintsContractCdd: 'CDD',
    constraintsContractFreelance: 'Indépendant',
    constraintsContractInterim: 'Intérim',
    constraintsContractAny: "N'importe quel contrat",
    constraintsSalaryLabel: 'Salaire mensuel brut minimum que vous accepteriez',
    constraintsSalaryHint: 'En euros par mois. Personne ne le voit, sauf le calcul des scores.',
    constraintsSalaryNoFloor: 'Pas de minimum',
    constraintsLanguagesLabel: 'Langues de travail',
    constraintsLanguagesHint: "Le niveau décide si un poste qui l'exige est un match.",
    constraintsLanguagesFormatHint:
      'Une par langue, avec le niveau entre parenthèses — french (native), english (fluent).',
    constraintsLanguageFrench: 'Français',
    constraintsLanguageDutch: 'Néerlandais',
    constraintsLanguageEnglish: 'Anglais',
    constraintsLevelChoose: 'Choisir un niveau',
    constraintsLevelNone: 'Je ne parle pas cette langue',
    constraintsLevelBasic: 'Basique',
    constraintsLevelIntermediate: 'Intermédiaire',
    constraintsLevelFluent: 'Courant',
    constraintsLevelNative: 'Natif',
    constraintsLanguagesNone: 'Je préfère ne pas préciser mes langues',
    constraintsSubmit: 'Construire mon profil',
    constraintsIncomplete:
      "Répondez à chaque question, ou cochez la case qui dit que cela ne vous limite pas. Une réponse vide serait lue comme « n'importe où ».",
    constraintsSaveFailed: "Nous n'avons pas pu enregistrer vos réponses. Réessayez.",
    constraintsOwnEyebrow: 'Vos limites fermes',
    baselineEyebrow: 'Le métier que vous exercez',
    baselineBody:
      "C'est le travail que vous faites maintenant, ou que vous avez fait en dernier — pas celui que vous cherchez. Il nous sert à distinguer un changement de métier d'un pas de côté, donc il doit être juste.",
    baselineRoleLabel: 'Votre métier',
    baselineDutiesLabel: 'Ce que ce métier comprend',
    baselineSectorsLabel: 'Secteurs où vous avez travaillé',
    baselineUnknownValue: 'Pas encore connu',
    baselineStateUnknown:
      "Nous n'avons pas pu lire un métier dans ce que vous nous avez donné. Dites-nous lequel, et indiquez sa source.",
    baselineStateUnconfirmed: 'Lu dans votre CV et votre profil. Confirmez-le, ou corrigez-le.',
    baselineStateConfirmed: 'Vous avez confirmé ceci.',
    baselineStateMissing: 'Aucun métier enregistré. Dites-nous lequel.',
    baselineConfirm: "Oui, c'est mon métier",
    baselineCorrect: 'Non, à corriger',
    baselineKeep: 'Laisser mon métier tel quel',
    baselineRoleTitleLabel: "Comment ce métier s'appelle-t-il",
    baselineSourceLabel: "D'où cela vient",
    baselineSourceHint:
      "Choisissez l'entrée sur laquelle ce métier repose. Nous ne gardons que ce que vous nous avez donné.",
    baselineNoSources: "Il n'y a encore rien d'enregistré sur quoi baser un métier.",
    baselineMixedCareer: "Mon parcours est mixte — il n'y a pas un seul métier",
    baselineListHint: 'Séparez chaque élément par une virgule.',
    matchSetupTitle: 'Configurez votre recherche',
    matchSetupBody:
      "Court et factuel, comme ci-dessus. Cela devient les règles exactes de votre recherche — si l'une d'elles ne vous limite pas, dites-le.",
    matchSetupLocationsLabel: 'Lieux où vous travailleriez',
    matchSetupLocationsHint: 'Cochez chaque province où vous pourriez travailler.',
    matchSetupLocationsNoPreference: "N'importe où me convient",
    matchSetupContractLabel: 'Contrats que vous accepteriez',
    matchSetupContractNoPreference: "N'importe quel contrat",
    matchSetupScheduleLabel: 'Horaires',
    matchSetupScheduleHint:
      'Dites-nous ce qui convient et ce qui ne convient pas — les deux sont utiles.',
    matchSetupScheduleAllowedLabel: 'Ça me convient',
    matchSetupScheduleExcludedLabel: 'Pas pour moi',
    matchSetupScheduleDay: 'Journée',
    matchSetupScheduleEvening: 'Soirée',
    matchSetupScheduleNight: 'Nuit',
    matchSetupScheduleWeekend: 'Week-end',
    matchSetupScheduleRotatingShift: 'Horaires tournants',
    matchSetupScheduleOnCall: 'Garde/astreinte',
    matchSetupScheduleNoPreference: "N'importe quel horaire convient",
    matchSetupPayLabel: 'Salaire minimum que vous accepteriez',
    matchSetupPayHint: "Le montant, puis la devise, la période, et si c'est brut ou net.",
    matchSetupPayAmountLabel: 'Montant',
    matchSetupPayCurrencyLabel: 'Devise',
    matchSetupPayPeriodLabel: 'Par',
    matchSetupPayPeriodHour: 'heure',
    matchSetupPayPeriodDay: 'jour',
    matchSetupPayPeriodMonth: 'mois',
    matchSetupPayPeriodYear: 'an',
    matchSetupPayPeriodUnknown: 'pas sûr',
    matchSetupPayGrossOrNetLabel: 'Brut ou net',
    matchSetupPayGross: 'Brut',
    matchSetupPayNet: 'Net',
    matchSetupPayGrossOrNetUnknown: 'Pas sûr',
    matchSetupPayNoPreference: 'Pas de minimum',
    matchSetupExclusionsLabel: 'Travail que vous ne ferez pas',
    matchSetupExclusionsHint:
      'Un par ligne — démarchage téléphonique, travail de nuit, ce qui vous concerne.',
    matchSetupEmployerLabel: 'Employeurs pour lesquels vous ne travaillerez pas',
    matchSetupEmployerHint:
      'Cochez seulement ce que vous voulez dire. Refuser les grands groupes '
      + "n'est pas refuser toute entreprise.",
    matchSetupEmployerAllCommercial: 'Toute entreprise commerciale',
    matchSetupEmployerLargeCorporate: 'Grands groupes',
    matchSetupEmployerStrengthLabel: 'Quelle est la force de ce choix ?',
    matchSetupEmployerStrengthHard: 'Ne me montrez jamais ces offres',
    matchSetupEmployerStrengthSoft: 'Montrez-les plus bas',
    matchSetupLanguagesLabel: 'Langues de travail',
    matchSetupLanguagesHint: "Le niveau décide si un poste qui l'exige est un match.",
    matchSetupSubmit: 'Enregistrer ma recherche',
    matchSetupIncomplete:
      "Répondez à chaque question, ou cochez la case qui dit que cela ne vous limite pas. Une réponse vide serait lue comme « n'importe où ».",
    matchSetupSaveFailed: "Nous n'avons pas pu enregistrer vos réponses. Réessayez.",
    reviewTitle: 'Votre profil',
    reviewBody: "Vérifiez et corrigez avant de continuer — c'est ce qui guide vos futurs matches.",
    reviewSourceEyebrow: 'Basé sur votre CV et votre entretien',
    reviewSummaryLabel: 'Résumé',
    reviewSkillsLabel: 'Compétences',
    reviewWantsLabel: 'Ce que vous recherchez',
    reviewIntentLabel: 'Ce qui vous amène',
    reviewFinish: 'Voir mes premiers matches',
    profileBuilding: 'Construction de votre profil',
    profileBuildingPatience:
      "Toujours en cours \u2014 c'est ce profil qui guidera tous vos futurs matches.",
    finalizeFailed:
      "Nous n'avons pas pu créer votre profil. Vos réponses sont conservées — réessayez.",
    saveFailed: "Nous n'avons pas pu enregistrer votre profil. Réessayez.",
    entryLoadFailed: "Nous n'avons pas pu déterminer où vous en étiez. Réessayez.",
    routeAfterInterviewFailed: "Nous n'avons pas pu déterminer la suite. Réessayez.",
    retry: 'Réessayer',
    policyReviewTitle: 'Approuvez votre recherche',
    policyReviewBody:
      'Voici la recherche exacte que nous allons lancer. Vérifiez-la, puis approuvez-la.',
    policyReviewLocationsLabel: 'Lieux',
    policyReviewContractLabel: 'Contrats',
    policyReviewScheduleLabel: 'Horaires',
    policyReviewPayLabel: 'Salaire minimum',
    policyReviewExclusionsLabel: 'Travail exclu',
    policyReviewDesiredWorkLabel: 'Travail recherché',
    policyReviewUnwantedWorkLabel: 'Travail à éviter',
    policyReviewNoPreference: 'Aucune préférence indiquée',
    policyReviewBlocked: 'Réponse nécessaire : {{reason}}',
    policyReviewBlockerGeneric: 'cette réponse',
    policyReviewEmployerLabel: 'Employeurs pour lesquels vous ne travaillerez pas',
    policyReviewEmployerAllCommercialHard:
      "Ne me montrez jamais une offre d'une entreprise commerciale.",
    policyReviewEmployerAllCommercialSoft:
      "Placez les offres d'entreprises commerciales plus bas dans ma liste.",
    policyReviewEmployerLargeCorporateHard:
      "Ne me montrez jamais une offre d'un grand groupe.",
    policyReviewEmployerLargeCorporateSoft:
      'Placez les offres des grands groupes plus bas dans ma liste.',
    policyReviewEmployerTypesHard:
      'Ne me montrez jamais une offre de ces types d\'employeur : {{types}}.',
    policyReviewEmployerTypesSoft:
      'Placez les offres de ces types d\'employeur plus bas dans ma liste : {{types}}.',
    policyReviewOrganizationPublicService: 'service public',
    policyReviewOrganizationNonprofit: 'association sans but lucratif',
    policyReviewOrganizationAcademic: 'université ou école',
    policyReviewOrganizationCommercial: 'entreprise commerciale',
    policyReviewOrganizationLargeCommercial: 'grand groupe',
    policyReviewEmployerOtherScope: 'Employeurs de ce type : {{scope}}.',
    policyReviewEmployerApprove: 'Approuver ce texte',
    policyReviewEmployerApproved: 'Approuvé',
    policyReviewEmployerApproveFailed:
      "Ce texte n'a pas pu être approuvé. Rechargez la page et réessayez.",
    policyReviewApprove: 'Approuver et continuer',
    policyReviewLoadFailed: "Nous n'avons pas pu charger votre recherche. Réessayez.",
    policyReviewChangedElsewhere:
      "Votre recherche a changé ailleurs — un autre onglet, ou depuis l'ouverture de cette page. Rechargez pour voir la version actuelle.",
    policyReviewApproveFailed: "Nous n'avons pas pu approuver votre recherche. Réessayez.",
    policyReviewReload: 'Recharger',
    upgradeEvidenceTitle: 'Relisez votre expérience',
    upgradeEvidenceBody:
      "Refaites l'entretien pour créer les preuves nécessaires à votre recherche. Votre profil reste inchangé jusqu'à la fin et au nouvel enregistrement. La nouvelle recherche commence après votre approbation.",
    upgradeEvidenceAction: "Refaire l'entretien",
    scoringTitle: 'Recherche de vos premiers matches',
    scoringBody: 'Nous comparons les offres en ligne à votre profil. Cela prend un instant.',
    scoringPatience:
      "Toujours en cours \u2014 chaque offre est lue en entier, d'o\u00f9 l'attente.",
    scoringStillGoing:
      "Certaines offres sont encore en cours d'analyse ; elles apparaîtront dès qu'elles seront prêtes.",
  },
  dashboard: {
    title: 'Vos matches du jour',
    matchCount: "{{count}} matches aujourd'hui",
    directTitle: 'Dans la lignée de votre recherche',
    lateralTitle: 'Les pas de côté',
    interested: 'Intéressé·e',
    notInterested: 'Pas intéressé·e',
    interestedMarked: 'Marqué intéressé·e',
    notInterestedMarked: 'Marqué pas intéressé·e',
    dryDayTitle: "Rien de solide aujourd'hui",
    dryDayBody:
      "On préfère vous dire honnêtement qu'il n'y a rien à la hauteur plutôt que de remplir la liste. Revenez demain.",
    refineLink: 'Quelque chose ne colle pas ? Affinez vos critères',
    preparingMatches: 'Pr\u00e9paration de vos premiers matches',
    preparingMatchesPatience:
      "Toujours en cours \u2014 chaque offre est lue en entier, d'o\u00f9 ce d\u00e9lai.",
    loadingPatience:
      'Toujours en cours \u2014 la lecture du jour compare \u00e0 votre profil les offres atteintes, ce qui prend une minute.',
    scoreExperience: 'Expérience',
    scoreValues: 'Valeurs',
    scoreSalary: 'Salaire',
    scoreNotDisclosed: 'non communiqué',
    tagDirect: 'Direct',
    tagLateral: 'Latéral',
    locationNotStated: 'Lieu de travail non précisé',
    viewPosting: "Voir l'offre",
    alsoOn: 'Aussi sur {{sources}}',
    showFullReasoning: 'Afficher le raisonnement complet',
    analysisLanguageNote: 'Cette explication est en {{language}}.',
    analysisLanguageFr: 'français',
    analysisLanguageNl: 'néerlandais',
    analysisLanguageEn: 'anglais',
    reportGone: "Cette offre n'existe plus",
    reportGoneSent: 'Signalé',
  },
  matcherV2: {
    recommendedTitle: 'Adéquations prouvées',
    shelfTitle: 'À regarder de plus près',
    shelfBody: 'Ces offres ont besoin de votre réponse avant de pouvoir être jugées.',
    tierStrong: 'Bonne adéquation',
    tierPossible: 'Adéquation possible',
    tierStretch: 'Adéquation à confirmer',
    tierUnresolved: 'Votre réponse est nécessaire',
    concernsHeading: 'À noter',
    notesHeading: 'Non vérifié',
    quotesHeading: "Extrait de l'offre",
    quoteSourceNote: "Cité tel quel, dans sa langue d'origine.",
    wouldApplyQuestion: 'Postuleriez-vous à cette offre ?',
    wouldApplyYes: 'Oui, je postulerais',
    wouldApplyYesMarked: 'Marqué : postulerait',
    wouldApplyNo: 'Non',
    wouldApplyNoMarked: 'Marqué : ne postulerait pas',
    wouldApplyNoNote: 'Pris en compte. Cela ne change aucun autre match.',
    checkQuestion: 'Aviez-vous déjà envisagé ce type de poste ?',
    checkYes: 'Oui',
    checkYesMarked: 'Marqué : oui, déjà envisagé',
    checkNo: 'Non',
    checkNoMarked: 'Marqué : non, pas envisagé',
    checkUnsure: 'Pas sûr·e',
    checkUnsureMarked: 'Marqué : pas sûr·e',
    missingFactHeading: 'Une chose que nous devons vous demander',
    missingFactQuestion: 'Savez-vous faire cela ?',
    missingFactYes: 'Oui, je sais',
    missingFactYesMarked: 'Marqué : vous savez faire',
    missingFactNo: 'Non, je ne sais pas',
    missingFactNoMarked: 'Marqué : vous ne savez pas faire',
    missingFactUnsure: 'Pas sûr·e',
    missingFactUnsureMarked: 'Marqué : pas sûr·e',
    missingFactUnsureNote: "Question laissée ouverte. Nous n'avons rien enregistré sur vous.",
    missingFactReassessing: 'Merci. Nous réexaminons cette offre avec votre réponse.',
    insufficientHeading: "Nous n'avons pas pu lire cette offre",
    insufficientBody:
      "Le texte ne suffisait pas pour juger cette offre par rapport à votre profil. Lisez-la vous-même et décidez.",
    insufficientLink: "Ouvrir l'offre",
    staleNote: 'Les informations de cette offre ont changé. Actualisation…',
    dryDayTitle: "Rien de prouvé aujourd'hui",
    dryDayBody:
      "On a terminé la lecture du jour et rien n'était à la hauteur. Revenez demain.",
    pendingTitle: 'Vérification en cours',
    pendingBody: "On vérifie encore les matches d'aujourd'hui par rapport à votre profil.",
    morePending: "D'autres matches sont encore en cours de vérification.",
    reasonDemandMet: 'Vous remplissez cette exigence.',
    reasonDemandMetUnresolved:
      'Cette exigence est probablement remplie, mais non confirmée par votre profil.',
    reasonDemandGap: 'Cette exigence n’est pas remplie.',
    reasonCoreDemandGapVerified: 'Une exigence essentielle est confirmée non remplie.',
    reasonCoreDemandGapUnresolved:
      'Une exigence essentielle n’est confirmée ni dans un sens ni dans l’autre.',
    reasonHardFailureVerified: 'Une exigence stricte est confirmée non remplie.',
    reasonHardFailureUnresolved:
      'Une exigence stricte n’est confirmée ni dans un sens ni dans l’autre.',
    reasonRuleConfirmed: 'Confirmé au regard de vos préférences déclarées.',
    reasonSoftRuleContradicted: "Ceci va à l'encontre d'une de vos préférences déclarées.",
    reasonUnverifiedFact: "L'offre ne le précise pas clairement.",
    reasonUnknownRuleFact: "Une préférence n'a pas pu être vérifiée.",
    reasonHardLocationConflict: 'Le lieu ne correspond pas à vos exigences.',
    reasonHardContractConflict: 'Le type de contrat ne correspond pas à vos exigences.',
    reasonHardWorkExclusionConflict: 'Ce type de poste figure sur votre liste d’exclusions.',
    reasonRequiredLanguageKnownLow:
      'Une langue requise est en dessous du niveau que vous avez déclaré.',
    reasonUnrecognized: 'Un nouveau type de remarque que nous ne savons pas encore décrire.',
    applyLink: "Ouvrir l\u2019offre pour postuler",
    employerUnverified: "L\u2019employeur n\u2019est pas indiqu\u00e9 dans l\u2019offre",
    recruiterLine: 'Annonce publi\u00e9e par {{recruiter}}',
    contractLine: 'Contrat : {{contract}}',
    careerHeading: 'Changement de carri\u00e8re',
    careerDirect: 'M\u00eame m\u00e9tier, m\u00eame type d\u2019employeur',
    careerSectorPivot: 'M\u00eame m\u00e9tier, un autre type d\u2019employeur',
    careerOccupationPivot: 'Un autre m\u00e9tier, m\u00eame type d\u2019employeur',
    careerOccupationAndSectorPivot: 'Un autre m\u00e9tier, un autre type d\u2019employeur',
    careerFamiliarOccupation: 'Un m\u00e9tier que vous avez d\u00e9j\u00e0 exerc\u00e9',
    careerUnresolved:
      "Nous n\u2019avons pas \u00e9tabli la comparaison avec votre parcours",
    careerSectorUnverified:
      "Le secteur de l\u2019employeur n\u2019est pas indiqu\u00e9 : la comparaison reste partielle.",
    explanationHeading: 'Pourquoi cette offre est ici',
    transferLine: 'Votre {{fact}} r\u00e9pond \u00e0 {{demand}} demand\u00e9 par ce poste.',
    blockingGap: '{{demand}} reste non v\u00e9rifi\u00e9.',
    nonblockingGap: '{{demand}} n\u2019est pas attest\u00e9. Cela ne bloque pas cette recommandation.',
    budgetDelayTitle: "Les v\u00e9rifications du jour sont en pause",
    budgetDelayBody:
      "Le budget de lecture du jour est \u00e9puis\u00e9 : le reste de vos offres attend demain. Rien n\u2019est perdu.",
    policyHoldTitle: 'En attente de votre nouvelle r\u00e8gle',
    policyHoldBody:
      "Vous avez approuv\u00e9 une nouvelle r\u00e8gle sur les employeurs. Les r\u00e9sultats d\u00e9cid\u00e9s avant elle sont retenus le temps d\u2019\u00eatre rev\u00e9rifi\u00e9s.",
    insufficientStateTitle: "Nous n\u2019avons pas pu lire les offres du jour",
    insufficientStateBody:
      "Les offres atteintes n\u2019en disaient pas assez pour les juger. Ouvrez-les et d\u00e9cidez vous-m\u00eame.",
  },
  savedSeen: {
    title: 'Enregistrés',
    emptyTitle: "Rien d'enregistré pour l'instant",
    emptyBody:
      'Marquez un match comme intéressant sur vos matches du jour et il arrivera ici, avec tout ce à quoi vous postulez.',
    sectionSaved: 'Offres enregistrées',
    sectionDismissed: 'Offres écartées',
    sectionNoLongerCurrent: 'Plus d’actualité',
    sectionNoLongerCurrentBody:
      "L’offre a disparu, ou elle ne répond plus à vos règles. Vos notes restent ; la recommandation, non.",
    staleReasonDelisted: 'L’annonce a été retirée.',
    staleReasonIneligible: 'Vos règles ne recommandent plus cette offre.',
    applicationStatusLabel: 'Statut de candidature',
    statusNone: 'Pas encore postulé',
    statusApplied: 'Candidature envoyée',
    statusResponded: 'Réponse reçue',
    statusInterview: 'Entretien',
    statusOffer: 'Offre',
    outcomePrompt: 'Des nouvelles de cette candidature ?',
    outcomeYes: 'Oui',
    outcomeNotYet: 'Pas encore',
  },
  apply: {
    tailorCta: 'Adapter le CV',
    tailoring: 'Rédaction d’un CV adapté…',
    tailoringPatience:
      'Toujours en cours — vérifier chaque ligne par rapport à votre CV prend un instant.',
    letterCta: 'Rédiger une lettre de motivation',
    writingLetter: 'Rédaction d’une lettre de motivation…',
    writingLetterPatience:
      'Toujours en cours — adapter votre lettre à cette offre prend un instant.',
    errorConsent:
      'Votre consentement doit être renouvelé avant d’utiliser votre CV. Vérifiez votre profil.',
    errorNoCv: "Ajoutez un CV à votre profil avant d'en adapter un pour une offre.",
    errorNoCvLetter:
      'Ajoutez un CV à votre profil avant de rédiger une lettre de motivation pour une offre.',
    errorBudget: "Vous avez atteint votre quota d'adaptations pour aujourd'hui. Réessayez demain.",
    reviewTitleCv: 'Relisez votre CV adapté',
    reviewTitleLetter: 'Relisez votre lettre de motivation',
    reviewHint: 'Modifiez ce que vous voulez avant d’enregistrer — rien n’est conservé avant cela.',
    save: 'Enregistrer',
    download: 'Télécharger en Markdown',
    backToDashboard: 'Retour au tableau de bord',
    noDraft: 'Aucun brouillon à relire. Commencez-en un depuis une offre du tableau de bord.',
  },
  profile: {
    title: 'Mon profil',
    formTitle: 'Vos informations',
    formBody: 'Modifiez ce que Pivot a enregistré sur vous.',
    saveChanges: 'Enregistrer les modifications',
    saved: 'Enregistré.',
    saveFailed:
      "Impossible d'enregistrer vos modifications pour l'instant. Rien n'a été modifié — réessayez.",
    redoInterview: "Refaire l'entretien",
    reviewMatcherV2: "Relire ma recherche d'emploi",
    redoConfirmBody:
      "Ceci relance l'inscription depuis le début — votre raison, le consentement, le CV et l'entretien lui-même. Votre profil enregistré reste inchangé jusqu'à ce que vous terminiez et l'enregistriez à nouveau.",
    redoConfirmGo: "Relancer l'inscription",
    redoFailed: "Impossible de relancer l'inscription. Réessayez.",
    rubricTitle: 'Comment vos matches sont notés',
    rubricBody:
      'Écrit à partir de votre CV et de votre entretien. Modifiez-en ce que vous voulez dans Affiner.',
    rubricVersion: 'Révision {{version}}',
    rubricMissing: "Terminez l'entretien : vos consignes de notation apparaîtront ici.",
    rubricNotStated: 'Vous ne nous l’avez pas encore dit.',
    rubricSummary: 'À quoi ressemble une bonne offre',
    rubricMustHaves: 'Une offre doit avoir',
    rubricDealBreakers: 'Une offre est écartée si',
    rubricStrengths: 'Ce que vous apportez',
    rubricNoEvidence: 'Aucune preuve au dossier',
    rubricProvenUnwanted: 'Prouvé, et pas le poste que vous voulez',
    rubricProvenUnwantedHelp:
      'Ça compte comme preuve que vous tenez le poste. Une offre qui en est surtout faite est notée bas.',
    rubricCommute: 'Où vous travaillerez',
    rubricCommuteFrom: 'Depuis',
    rubricCommuteWithin: 'Jusqu’à {{minutes}} min',
    rubricCommuteBy: 'En',
    rubricCommuteRegions: 'Régions',
    rubricCommuteExamples: 'Par exemple',
    rubricAxesHeading: 'Ce qui pèse dans la note',
    rubricAxisExperience: 'Note expérience',
    rubricAxisValues: 'Note valeurs',
    rubricAxisSalary: 'Note salaire',
    rubricShare: '{{share}} %',
    rubricLateral: 'Pas de côté qui valent le détour',
    rubricTechnicalDepth: 'Votre niveau technique',
    refineTitle: 'Affiner',
    refineBody:
      'Dites au conseiller ce que la notation ci-dessus rate. Il a lu vos consignes et les offres que vous avez marquées intéressé·e ou pas intéressé·e.',
    refinePlaceholder: 'Ex. « il y a encore trop de vente au sol là-dedans »…',
    refineSend: 'Envoyer',
    refining: 'Lecture de vos consignes et de vos matches',
    refiningPatience: 'Toujours en cours — il réécrit les consignes, il ne fait pas que répondre.',
    refineYou: 'Vous',
    refineAdviser: 'Conseiller',
    refineNoChange: "Rien n'a changé pour l'instant — répondez ci-dessus et ça changera.",
    refineApplied: 'Appliqué dès vos prochains matches.',
    refineUndo: 'Annuler',
    refineUndone: 'Annulé',
    refineOpener:
      "Qu'est-ce que la notation rate ? Une offre bien notée que vous ne prendriez jamais, c'est ce qui m'aide le plus.",
    exportTitle: 'Télécharger mes données',
    exportBody:
      "Tout ce que Pivot détient sur vous, dans un seul fichier JSON : votre profil, vos scores, l'historique de vos consentements et notifications, et l'analyse de votre CV (pas le fichier lui-même). Utile avant de supprimer votre compte, ou à tout moment pour voir exactement ce qui est conservé.",
    exportButton: 'Télécharger mes données',
    exportFailed: "Nous n'avons pas pu préparer votre export. Réessayez.",
    signOutEverywhereTitle: 'Se déconnecter partout',
    signOutEverywhereBody:
      "Met fin à toutes les sessions ouvertes sur votre compte, y compris celle-ci. Toute autre personne connaissant votre mot de passe est déconnectée aussi. Reconnectez-vous quand vous voulez — rien d'autre ne change.",
    signOutEverywhereButton: 'Se déconnecter partout',
    signOutEverywhereFailed:
      "Nous n'avons pas pu déconnecter vos autres sessions. Vous êtes toujours connecté ici — réessayez.",
    changePasswordTitle: 'Changer de mot de passe',
    changePasswordBody:
      'Nous vous envoyons un lien de réinitialisation par e-mail au lieu de demander un nouveau mot de passe ici. Le lien sert une seule fois, pendant 30 minutes, et définir le nouveau mot de passe déconnecte toutes vos autres sessions.',
    changePasswordButton: 'Envoyez-moi un lien',
    changePasswordSent:
      'Nous avons envoyé un lien de réinitialisation à {{email}}. Il sert une seule fois, pendant 30 minutes.',
    changePasswordFailed: "Nous n'avons pas pu envoyer le lien. Réessayez.",
    deleteTitle: 'Supprimer mon compte',
    deleteBody:
      'Cette action supprime votre compte, votre profil, vos scores, votre consentement et votre fichier CV chiffré, en une seule opération. Elle est immédiate et définitive : aucun délai de grâce, aucune récupération possible.',
    deleteStart: 'Supprimer mon compte',
    deleteConfirmLabel: 'Je comprends que cette suppression est définitive et irréversible.',
    deleteConfirm: 'Tout effacer maintenant',
    deleteFailed: "Nous n'avons pas pu supprimer votre compte. Rien n'a été effacé — réessayez.",
    privacyLink: 'Ce que nous conservons, et pourquoi — la politique de confidentialité',
    accountSectionLabel: 'Votre compte',
  },
  privacy: {
    title: 'Politique de confidentialité',
    version: 'Version {{version}} · dernière mise à jour {{updated}}',
    intro:
      "Ce texte est en langage clair, ce n'est pas un contrat juridique. Il décrit ce que Pivot fait réellement de vos données.",
    whatTitle: "Ce qu'est Pivot",
    whatBody:
      "Une application de recherche d'emploi. Vous fournissez votre CV et répondez à un court entretien ; l'application compare votre profil aux offres belges collectées et les classe avec des modèles d'IA configurés.",
    collectTitle: 'Ce que nous collectons, et pourquoi',
    collectCv:
      "Votre CV (PDF) — pour construire votre profil. Chiffré, dans Google Cloud Storage ; la base de données ne conserve qu'une référence, jamais le fichier.",
    collectProfile:
      'Votre profil structuré (compétences, expérience, préférences) — ce à quoi les offres sont comparées.',
    collectAnswers:
      "Vos réponses pendant l'entretien — intégrées à ce profil. La transcription brute n'existe que le temps de votre onboarding : supprimée dès que vous le terminez, ou après 30 jours si vous ne le terminez jamais.",
    collectAccount: 'Votre e-mail et votre identifiant de compte — pour la connexion.',
    collectInterest:
      'Les offres que vous marquez intéressé·e ou pas intéressé·e — pour affiner vos futurs matches.',
    collectConsent:
      'La preuve de votre consentement (version de la politique et date) — pour prouver votre accord avant tout traitement de votre CV.',
    collectNever:
      "Nous ne collectons aucune donnée de paiement, ne vendons ni ne partageons vos données avec des annonceurs, et ne les utilisons pas pour entraîner un modèle d'IA.",
    anthropicTitle: "Fournisseurs d'IA",
    anthropicBody:
      "Pivot peut envoyer le texte de votre CV, vos réponses, votre profil structuré, le texte de recherche dérivé du profil, les offres utiles, ainsi que les requêtes et réponses liées à Anthropic, OpenAI, Voyage AI ou OpenRouter. OpenRouter peut transmettre la requête à un fournisseur d'inférence nommé dans cette politique. Pivot n'a pas encore d'accord de traitement des données avec ces fournisseurs d'IA.",
    residencyTitle: 'Où vivent vos données',
    residencyBody:
      "La base de données, le stockage des CV et l'application elle-même tournent dans la région europe-west1 (Belgique) de Google Cloud.",
    storageTitle: 'Cookies et stockage local',
    storageBody:
      "Pivot ne dépose aucun cookie qui lui soit propre, et n'utilise ni mesure d'audience, ni publicité, ni traqueur tiers. Deux éléments sont conservés dans votre navigateur, tous deux strictement nécessaires au fonctionnement de l'application :",
    storageSession:
      "Votre jeton de connexion, pour rester connecté d'une visite à l'autre. Supprimé à la déconnexion.",
    storageLocale: 'La langue que vous avez choisie — français, néerlandais ou anglais.',
    storageGoogle:
      "Le bouton « Se connecter avec Google » exécute un script servi par accounts.google.com. Ce script appartient à Google, qui peut lire ou déposer ses propres cookies sur google.com pendant son exécution. Il se charge dès l'ouverture d'une page comportant ce bouton, et non au moment où vous cliquez. La connexion par e-mail l'évite entièrement.",
    storageControl:
      "Effacer les données de votre navigateur supprime l'ensemble. Rien de tout cela ne sert à vous profiler, rien n'est partagé avec qui que ce soit.",
    consentTitle: 'Consentement',
    consentBody:
      "Avant que Pivot ne lise votre CV ou n'envoie des données personnelles à un fournisseur d'IA, votre consentement explicite est demandé. Les comptes actuels de testeurs et d'amis ne renouvellent pas leur consentement pour cette mise à jour, et Pivot supprimera toutes les données de développement. Une nouvelle politique et un consentement explicite sont requis avant l'inscription d'une personne qui n'est pas une amie.",
    erasureTitle: "Droit à l'effacement",
    erasureBody:
      "Vous pouvez supprimer votre compte et toutes les données associées à tout moment, via le bouton « Supprimer mon compte » sur votre page de profil. Ce n'est pas une suppression logique : compte, profil, scores, consentements, historique de notifications et fichier CV chiffré sont supprimés dans la même opération. Rien n'est récupérable ensuite — exportez d'abord ce que vous voulez garder.",
    minimizationTitle: 'Minimisation des données',
    minimizationBody:
      "Nous ne conservons que ce qui est nécessaire au matching. La transcription d'entretien n'existe que le temps de votre onboarding — supprimée dès qu'il est terminé, ou après 30 jours s'il ne l'est jamais — et les offres ne sont gardées que tant qu'elles sont en ligne.",
    contactTitle: 'Qui contacter',
    contactBody:
      "En attendant que Pivot dispose d'un contact dédié à la vie privée, joignez l'équipe par le canal utilisé lors de votre inscription.",
    statusTitle: 'Statut de cette politique',
    statusBody:
      "Politique de développement pour des testeurs et des amis. Elle n'a pas fait l'objet d'une relecture juridique, et aucun accord de traitement des données n'est en place avec les fournisseurs d'IA approuvés. Ces éléments sont requis avant l'inscription d'une personne qui n'est pas une amie.",
    back: 'Retour',
  },
  a11y: {
    matchScores: 'Scores de correspondance',
    scoreBand: '{{band}} sur {{max}}',
    languageSwitch: 'Langue',
    logo: 'Logo Pivot',
    onboardingSteps: 'Étapes de la configuration',
    interviewAnswer: 'Votre réponse',
    interviewMultiOptions: 'Options de réponse — choisissez-en une ou plusieurs',
    themeToggleToDark: 'Passer au thème sombre',
    themeToggleToLight: 'Passer au thème clair',
    applyReviewTextarea: 'Contenu du CV adapté',
    skipToMain: 'Passer au contenu principal',
    authHomeLink: "Pivot — retour à l'accueil",
  },
}

export default fr
